import React, { useState, useEffect, useRef } from 'react';
import { Modal, TextField, Banner } from '@shopify/polaris';

/**
 * Modal asking the user for a name for a new Saved View.
 * onSave(name) → returns Promise. Server returns 409 if name already exists.
 */
export default function SaveViewModal({ open, onClose, onSave, existingNames = [] }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName('');
      setError('');
      setSaving(false);
      // Focus the input after Polaris finishes mounting
      setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
    }
  }, [open]);

  const trimmed = name.trim();
  const localDup = existingNames.some(
    n => (n || '').toLowerCase() === trimmed.toLowerCase()
  );

  const submit = async () => {
    if (!trimmed) { setError('Name is required'); return; }
    if (localDup) { setError('A saved view with that name already exists'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(trimmed);
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not save view');
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Save current view"
      primaryAction={{
        content: 'Save',
        onAction: submit,
        loading: saving,
        disabled: !trimmed || localDup,
      }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        {error && (
          <div style={{ marginBottom: 12 }}>
            <Banner tone="critical">{error}</Banner>
          </div>
        )}
        <TextField
          label="View name"
          value={name}
          onChange={setName}
          autoComplete="off"
          placeholder="e.g. Open in-store pickups"
          ref={inputRef}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
        />
      </Modal.Section>
    </Modal>
  );
}
